"use client";

import { useCallback } from "react";

import { useTotem } from "@/app/(users)/totem-by-slug/[slug]/providers/totem-provider";
import type { CartSubselection } from "@/app/(users)/totem-by-slug/[slug]/types/cart-item";
import type { MenuSnapshotProduct } from "@/app/(users)/totem-by-slug/[slug]/types/menu-snapshot";
import { shouldOpenProductSheet } from "@/app/(users)/totem-by-slug/[slug]/utils/product-interaction";
import { getProductDisplayName } from "@/app/(users)/totem-by-slug/[slug]/utils/product-name";
import { getProductStockLimit } from "@/app/(users)/totem-by-slug/[slug]/utils/product-stock";

export function useProductInteraction() {
  const { cart, setSelectedProduct, triggerFlyToCart } = useTotem();

  const addProductToCart = useCallback(
    (
      product: MenuSnapshotProduct,
      quantity: number,
      subselections: CartSubselection[],
      fromRect?: DOMRect,
    ) => {
      const stockLimit = getProductStockLimit(product.product_stock);
      const currentQuantity = cart.items
        .filter((item) => item.productId === product.id)
        .reduce((sum, item) => sum + item.quantity, 0);

      if (currentQuantity + quantity > stockLimit) {
        return;
      }

      cart.addItem({
        productId: product.id,
        name: getProductDisplayName(product),
        imageUrl: product.imageurl,
        unitPrice: Number(product.price),
        quantity,
        subselections,
      });

      if (fromRect && product.imageurl) {
        triggerFlyToCart({
          imageUrl: product.imageurl,
          fromRect,
        });
      }
    },
    [cart, triggerFlyToCart],
  );

  const handleProductClick = useCallback(
    (product: MenuSnapshotProduct, fromRect: DOMRect) => {
      const stockLimit = getProductStockLimit(product.product_stock);
      const currentQuantity = cart.items
        .filter((item) => item.productId === product.id)
        .reduce((sum, item) => sum + item.quantity, 0);

      if (currentQuantity >= stockLimit) {
        return;
      }

      if (shouldOpenProductSheet(product, cart.items)) {
        setSelectedProduct(product);
        return;
      }

      addProductToCart(product, 1, [], fromRect);
    },
    [cart.items, setSelectedProduct, addProductToCart],
  );

  return {
    handleProductClick,
    addProductToCart,
  };
}
